import React, { ChangeEventHandler, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Space,
  Spin,
  Switch,
  Tabs,
  Layout,
  theme,
  Breadcrumb,
  Divider,
  Select,
  Result,
  Popconfirm,
  message,
  Modal,
  Menu,
  Dropdown,
  MenuProps
} from 'antd';
import { SwitchChangeEventHandler } from 'antd/es/switch';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router';
import { debounce } from 'lodash';
import { Link } from 'react-router-dom';
import {
  DeleteFilled,
  QuestionCircleOutlined,
  LoadingOutlined,
  PlusOutlined,
  PlusCircleOutlined,
  DownOutlined
} from '@ant-design/icons';
import {
  putRatingBlurbSection,
  putProjectReportCatcherStatus,
  extractSDGFromGoogleSpreadsheet,
  getProjectLiveVersions,
  restoreProductionData,
  createCatcherTemplate,
  handleDeepDiveToggle,
  getUser,
  cloneCatcherData,
  addMissingCaptions
} from '../api';
import { getGHGStandards } from '../api/ghg';

import axiosInstance from '../api/axios';
import TableEditor from '../components/TableEditor';
import useAuth from '../hooks/usePropelAuth';
import { convertGHGRatingFromNumbertoText, convertSDGRatingFromNumbertoText } from '../utils';
import ContentEditor from '../components/ContentEditor';
import TimelineTableEditor from '../components/TimelineTableEditor';
import ReadMoreBlurbEditor from '../components/ReadMoreBlurbEditor';

const { Header } = Layout;

enum ProjectStatus {
  REPORT_STAGING = 'REPORT_STAGING',
  REPORT_PRODUCTION_LIVE = 'REPORT_PRODUCTION_LIVE'
}

enum ProjectStatusEnum {
  DRAFT = 'draft',
  STAGING = 'staging',
  PRODUCTION = 'production'
}

const BTN_TEXT = {
  REPORT_STAGING: 'Staging',
  REPORT_PRODUCTION_LIVE: 'Production'
};

// const ProjectStateMachine = () => {
//   return {
//     transitions: {
//       [ProjectStatusTransitions.SUBMIT_FOR_REVIEW_OWNER]: {
//         from: ProjectStatus.ANALYZE,
//         to: ProjectStatus.REVIEW_OWNER
//       },
//       [ProjectStatusTransitions.SUBMIT_FOR_REVIEW_PRE_LIVE]: {
//         from: ProjectStatus.REVIEW_OWNER,
//         to: ProjectStatus.REVIEW_PRE_LIVE
//       },
//       [ProjectStatusTransitions.SUBMIT_FOR_CHANGES_AND_GO_LIVE]: {
//         from: ProjectStatus.REVIEW_PRE_LIVE,
//         to: ProjectStatus.READY_FOR_LIVE
//       },
//       [ProjectStatusTransitions.GO_LIVE]: {
//         from: ProjectStatus.READY_FOR_LIVE,
//         to: ProjectStatus.GO_LIVE
//       },
//       [ProjectStatusTransitions.RESUBMIT_FOR_APPROVAL]: {
//         from: ProjectStatus.READY_FOR_LIVE,
//         to: ProjectStatus.REVIEW_PRE_LIVE
//       }
//     }
//   };
// };

// const possibleTransitions = (status: ProjectStatus) => {
//   const stateMachine = ProjectStateMachine();

//   return Object.entries(stateMachine.transitions)
//     .filter(([, transition]) => transition.from === status)
//     .map(([transition]) => transition);
// };

type RatingBlurbFieldType = {
  field: any;
  project: any;
  entityId: string;
  queryKey: string;
  workflowState?: any;
  onTitleChange: (item: any) => ChangeEventHandler<HTMLElement>;
  onToggleTitle: (checked: boolean) => SwitchChangeEventHandler | undefined;
  onSaveContent: (item: any, sectionType: string) => (data: string) => void | undefined;
  handleDeepDiveToggleWithMessage: (creditingPeriodId: string, key: string, value: boolean) => void;
};

const RatingBlurbChildren: React.FC<RatingBlurbFieldType> = ({
  field,
  project,
  entityId,
  queryKey,
  workflowState,
  handleDeepDiveToggleWithMessage,
  onTitleChange,
  onToggleTitle,
  onSaveContent
}) => {
  if (!field.children || field.children.length === 0) return null;

  if (field.children.length === 1) {
    return (
      <RatingBlurbField
        field={field.children[0]}
        project={project}
        entityId={entityId}
        queryKey={queryKey}
        handleDeepDiveToggleWithMessage={handleDeepDiveToggleWithMessage}
        onTitleChange={onTitleChange}
        onSaveContent={onSaveContent}
        onToggleTitle={onToggleTitle}
      />
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        <Switch
          checked={!!project?.project_crediting_periods[0]?.ratings?.enable_ghg_deep_dive}
          disabled={workflowState === ProjectStatusEnum.PRODUCTION}
          onChange={(checked) =>
            handleDeepDiveToggleWithMessage(
              project.project_crediting_periods[0].id,
              'enable_ghg_deep_dive',
              checked
            )
          }
        />
        <span>Risk Factor Summary</span>
      </div>
      <Tabs
        defaultActiveKey="0"
        onChange={(key: string) => console.log(key)}
        items={field.children
          .sort((a, b) => a.order - b.order || a.id - b.id)
          .map((c, ci) => {
            return {
              key: `${ci}`,
              label: c.title,
              children: (
                <RatingBlurbField
                  key={ci}
                  field={c}
                  project={project}
                  entityId={entityId}
                  queryKey={queryKey}
                  handleDeepDiveToggleWithMessage={handleDeepDiveToggleWithMessage}
                  onTitleChange={onTitleChange}
                  onSaveContent={onSaveContent}
                  onToggleTitle={onToggleTitle}
                />
              )
            };
          })}
      />
    </>
  );
};

const RatingBlurbField: React.FC<RatingBlurbFieldType> = ({
  field,
  project,
  entityId,
  queryKey,
  workflowState,
  handleDeepDiveToggleWithMessage,
  onTitleChange,
  onToggleTitle,
  onSaveContent
}) => {
  if (field.blurb_type === 'read_more') {
    return (
      <ReadMoreBlurbEditor
        field={field}
        project={project}
        entityId={entityId}
        queryKey={queryKey}
        onSaveContent={onSaveContent}
        onTitleChange={onTitleChange}
        onToggleTitle={onToggleTitle}
      />
    );
  }

  return (
    <>
      <div className="flex flex-col">
        <div className={`flex gap-4 items-center mb-4 ${field.title_visible ? '' : 'hidden'}`}>
          <div className="text-md font-bold">{field.title}</div>
        </div>
        {field.rating_blurb_sections
          .sort((a, b) => a.order - b.order || a.id - b.id)
          .map((rating_blurb_section, index) => {
            let sectionType = rating_blurb_section.type;
            let item = rating_blurb_section.rating_blurb_section_paragraph;
            if (!item) {
              item = rating_blurb_section.rating_blurb_section_table;
            }
            if (!item) return null;

            return (
              <div key={index}>
                <div
                  className={`flex gap-4 items-center mb-4 ${item.title_visible ? '' : 'hidden'}`}>
                  {item.title_enabled ? (
                    <div className="text-md font-bold mb-4">{item.title}</div>
                  ) : null}
                </div>
                <div className="flex gap-4 items-center mb-4 flex-1">
                  {sectionType === 'paragraph' && (
                    <div className="flex-1">
                      <ContentEditor
                        saveData={onSaveContent(item, sectionType)}
                        key={index}
                        sectionType={sectionType}
                        entityId={entityId}
                        queryKey={queryKey}
                        id={item.id}
                        project={project}
                        data={item.ckeditor_content_doc}
                      />
                    </div>
                  )}
                  {sectionType === 'table' && (
                    <div className="flex-1">
                      <TableEditor
                        project={project}
                        key={index}
                        id={item.id}
                        queryKey={queryKey}
                        section={rating_blurb_section}
                        item={item}
                        entityId={entityId}
                        saveData={onSaveContent(item, sectionType)}
                      />
                    </div>
                  )}
                  {sectionType === 'timeline_table' && (
                    <div className="flex-1">
                      <TimelineTableEditor
                        project={project}
                        key={index}
                        id={item.id}
                        queryKey={queryKey}
                        section={rating_blurb_section}
                        item={item}
                        entityId={entityId}
                        saveData={onSaveContent(item, sectionType)}
                      />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
      </div>
      <RatingBlurbChildren
        project={project}
        field={field}
        entityId={entityId}
        queryKey={queryKey}
        workflowState={workflowState}
        handleDeepDiveToggleWithMessage={handleDeepDiveToggleWithMessage}
        onTitleChange={onTitleChange}
        onSaveContent={onSaveContent}
        onToggleTitle={onToggleTitle}
      />
    </>
  );
};

const WithProjectIdDetails = ({ project, queryKey }) => {
  const [fields, setFields] = useState<Array<any>>([]);
  const [sdgLink, setSDGLink] = useState<string>('');
  const client = useQueryClient();
  const auth = useAuth();
  const [loading, setLoading] = useState(false);
  const [workflowState, setWorkflowState] = useState<ProjectStatusEnum>(ProjectStatusEnum.STAGING);
  const [disableCatcherTemplateButton, setDisableCatcherTemplateButton] = useState(false);
  const [formClone] = Form.useForm();
  const [ghgStandards, setGHGStandards] = useState<any>([]);

  useEffect(() => {
    setWorkflowState(project.project_crediting_periods?.[0]?.project_workflow?.state);
  }, [project.project_crediting_periods]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (auth?.roles?.includes('internal_tool:create_template_access')) {
          setDisableCatcherTemplateButton(false);
        } else {
          setDisableCatcherTemplateButton(true);
        }
        const ghgStandards = await getGHGStandards();
        setGHGStandards(ghgStandards.response);
      } catch (error) {
        console.error('Error fetching user data:', error);
      }
    };

    fetchData();
  }, []);

  const [cloneCatcherDataModalOpen, setCloneCatcherDataModalOpen] = useState(false);

  const handleClone = () => {
    setCloneCatcherDataModalOpen(true);
  };

  const handleCloneCatcherDataModalOk = async () => {
    try {
      const formData = formClone.getFieldsValue();
      await formClone.validateFields();
      try {
        setCloneCatcherDataModalOpen(false);
        setLoading(true);
        const response = await cloneCatcherData({
          selectedProjectId: Number(formData.selectedProjectId),
          selectedGhgCode: formData.selectedGhgCode,
          selectedCreditingPeriod: Number(formData.selectedCreditingPeriod),
          creditingPeriodId: project.project_crediting_periods[0].id
        });
        setLoading(false);
        message.success('Cloned the Catcher Data successfully');
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 500));
        window.location.reload();
      } catch (err: any) {
        console.log(`Error cloning Catcher Data: ${err}`);
        setLoading(false);
        message.error({
          content: err.message || 'Unable to clone Catcher Data',
          duration: 4,
          style: { textAlign: 'right' }
        });
      }
    } catch (err) {
      console.log('error', err);
      message.error({
        content: 'Please fill in all required fields before proceeding.',
        duration: 4,
        style: { textAlign: 'right' }
      });
    }
  };

  const [addMissingCaptionOpen, setAddMissingCaptionOpen] = useState(false);
  const { id, creditingPeriodId }: any = useParams<{ id: string; creditingPeriodId: string }>();

  const handleMissingCaption = () => {
    setAddMissingCaptionOpen(true);
  };

  const handleMissingCaptionModalOk = async () => {
    try {
      try {
        setAddMissingCaptionOpen(false);
        setLoading(true);
        const response = await addMissingCaptions(id, creditingPeriodId);
        setLoading(false);
        message.success('Missing Caption Fields added.');
        setLoading(true);
        await new Promise((resolve) => setTimeout(resolve, 500));
        window.location.reload();
      } catch (err: any) {
        console.log(`Error adding missing caption fields: ${err}`);
        setLoading(false);
        message.error({
          content: err.message || 'Unable to add missing caption fields',
          duration: 4,
          style: { textAlign: 'right' }
        });
      }
    } catch (err: any) {
      console.log('error', err);
      message.error({
        content: err.message || 'Unable to add missing caption fields',
        duration: 4,
        style: { textAlign: 'right' }
      });
    }
  };

  const {
    isLoading: isLiveVersionsLoading,
    isError,
    data: liveVersions
  } = useQuery(
    `${queryKey}_liveVersions`,
    getProjectLiveVersions({
      projectId: project.project_id,
      creditingPeriodId: project.project_crediting_periods[0].crediting_period_id,
      ghgStandardId: project.ghg_standard?.id
    })
  );

  const {
    mutate: updateRatingBlurbSection,
    isLoading,
    error
  } = useMutation(putRatingBlurbSection, {
    onSuccess: (data) => {
      client.invalidateQueries(queryKey);
    }
  });

  const {
    mutate: updateProjectReportCatcherStatus,
    isLoading: isReportCatcherLoading,
    error: reportCatcherError
  } = useMutation(putProjectReportCatcherStatus, {
    onSuccess: (data) => {
      client.invalidateQueries(queryKey);
    }
  });

  const {
    mutate: parseSDGSpreadsheet,
    isLoading: isSDGExtractionLoading,
    error: sdgExtractionError
  } = useMutation(extractSDGFromGoogleSpreadsheet, {
    onSuccess: (data) => {
      // client.invalidateQueries(queryKey);
    }
  });

  const {
    mutate: restoreProductionDataFromLatestLive,
    isLoading: isRestoringProductionDataLoading,
    error: restoringProductionDataError
  } = useMutation(restoreProductionData, {
    onSuccess: (data) => {
      message.success('Data restored successfully!');
      client.invalidateQueries(queryKey);
    }
  });

  const { ghg_standard, project_id, project_crediting_periods = [] } = project;
  const [project_crediting_period] = project_crediting_periods;

  const { crediting_period_id } = project_crediting_period;
  const project_type = project.project_types?.[0]?.project_type?.display_name;
  const project_type_group = project.project_types?.[0]?.project_type?.project_group?.display_name;

  const entityId = `${ghg_standard.code}_${project_id}_${crediting_period_id}`;

  useEffect(() => {
    setFields(project_crediting_period.rating_blurbs);
  }, [project_crediting_periods]);

  const onSaveContent = (item: any, sectionType: string) => {
    return (data: any) => {
      return updateRatingBlurbSection({
        ratingBlurbSectionId: item.id,
        sectionType,
        data
      });
    };
  };

  const onTitleChange = (item) => (evt) => {
    const title = evt.target.value;
    updateRatingBlurbSection({
      ratingBlurbSectionId: item.id,
      sectionType: 'paragraph',
      data: {
        title
      }
    });
  };

  const onDebouncedTitleChange = (item) => debounce(onTitleChange(item), 1000);

  const onToggleTitle = (item) => (checked: Boolean) => {
    updateRatingBlurbSection({
      ratingBlurbSectionId: item.id,
      sectionType: 'paragraph',
      data: {
        title_visible: !!checked
      }
    });
  };

  const updateProjectStatus = (nextState) => {
    return updateProjectReportCatcherStatus({
      projectId: project_id,
      creditingPeriodId: crediting_period_id,
      ghgStandard: ghg_standard.id,
      data: {
        status: nextState
      }
    });
  };

  const onSDGInputChange = (evt) => {
    setSDGLink(evt.target.value);
  };

  const onSDGParse = () => {
    parseSDGSpreadsheet({
      projectId: project_id,
      creditingPeriodId: crediting_period_id,
      ghgStandard: ghg_standard,
      sheetUrl: sdgLink
    });
  };

  const onRestoreDataFromProduction = (projectRatingsBlurbLiveId) => () => {
    restoreProductionDataFromLatestLive({
      projectRatingsBlurbLiveId
    });
  };

  const [createCatcherTemplateOpen, setCreateCatcherTemplateOpen] = useState(false);
  const handleCreateCatcherTemplate = () => {
    setCreateCatcherTemplateOpen(true);
  };

  const handleCreateCatcherTemplateModalOK = async () => {
    try {
      setCreateCatcherTemplateOpen(false);
      setLoading(true);
      const response = await createCatcherTemplate({
        project_id: project.id,
        crediting_period_id: project.project_crediting_periods[0].id
      });
      setLoading(false);
      message.success(`Triggered Creation of Catcher Template`);
      window.location.reload();
    } catch (err) {
      console.log(`error in create catcher template api: ${err}`);
      message.error({
        content: 'Error in creating catcher template',
        duration: 4,
        style: { textAlign: 'right' }
      });
    }
  };

  const liveVersionsData = liveVersions?.data?.data;
  const items: MenuProps['items'] = [
    {
      key: '1',
      label: 'Create Catcher Template',
      icon: <PlusOutlined />,
      onClick: handleCreateCatcherTemplate,
      style: {
        display:
          workflowState === ProjectStatusEnum.PRODUCTION || disableCatcherTemplateButton
            ? 'none'
            : 'block'
      }
    },
    {
      key: '2',
      label: 'Clone Catcher Data',
      icon: <PlusCircleOutlined />,
      onClick: handleClone
    },
    {
      key: '3',
      label: 'Add Missing Captions Fields',
      icon: <PlusCircleOutlined />,
      onClick: handleMissingCaption,
      style: {
        display: !auth?.roles.includes('internal_tool:super_admin') ? 'none' : 'block'
      }
    }
  ];

  const [deepDiveMessage, setDeepDiveMessage] = useState('');

  const handleDeepDiveToggleWithMessage = async (creditingPeriodId, key, value) => {
    try {
      const response = await handleDeepDiveToggle(creditingPeriodId, key, value);
      client.invalidateQueries(queryKey);
      setDeepDiveMessage(response);
      message.success(response);
    } catch (error) {
      message.error('Failed to update deep dive setting');
    }
  };

  return (
    <Card>
      <div className="mb-4 pb-3 border-0 border-b border-gray-300 border-solid">
        <Space.Compact style={{ width: '100%' }} className="mb-2">
          <Input
            addonBefore="SDG Link"
            placeholder="Paste SDG Google spreadsheet link of this project"
            value={sdgLink}
            onChange={onSDGInputChange}
          />
          <Button type="primary" onClick={onSDGParse}>
            Submit
          </Button>
        </Space.Compact>
        <span>
          Please paste the SDG Spreadsheet in the input box above and click submit to parse the SDG
          spreadsheet and move the data to our database. The SDG spreadsheet should be shared with
          calyx-sdg-parser@soy-enterprise-396707.iam.gserviceaccount.com with Viewer permissions
        </span>
        <div className="flex items-center gap-2 my-3">
          <Switch
            checked={!!project_crediting_period?.ratings?.enable_sdg_deep_dive}
            disabled={workflowState === ProjectStatusEnum.PRODUCTION}
            onChange={(checked) =>
              handleDeepDiveToggleWithMessage(
                project.project_crediting_periods[0].id,
                'enable_sdg_deep_dive',
                checked
              )
            }
          />
          <span>Enable SDG Deep Dive</span>
        </div>
      </div>
      <div className="flex gap-3 items-center justify-between mb-4 pb-3 border-0 border-b border-gray-300 border-solid">
        <div className="ml-2">
          <span className="text-gray-500">Type (Group): </span> {project_type} ({project_type_group}
          )
        </div>
        <div className="flex gap-3 items-center">
          {liveVersionsData?.id && (
            <Popconfirm
              title={'Restore data from production'}
              description="This would replace all the current information with the latest data from production"
              icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
              onConfirm={onRestoreDataFromProduction(liveVersionsData?.id)}
              okText="Yes, restore and override"
              cancelText="No, do not restore">
              {isRestoringProductionDataLoading ? (
                <Button type="default">
                  <Spin size="small">Restoring data...</Spin>
                </Button>
              ) : (
                <Button type="default" disabled={isReportCatcherLoading}>
                  Restore data from production
                </Button>
              )}
            </Popconfirm>
          )}
          <Dropdown menu={{ items }} trigger={['click']}>
            <Button type="default" hidden={workflowState === ProjectStatusEnum.PRODUCTION}>
              Actions <DownOutlined />
            </Button>
          </Dropdown>
          <Modal
            title="Create New Catcher Template"
            open={createCatcherTemplateOpen}
            onOk={handleCreateCatcherTemplateModalOK}
            onCancel={() => setCreateCatcherTemplateOpen(false)}
            style={{ top: '20%' }}>
            <div>
              <p>
                Upon confirmation, this will create new catcher template. <br></br>
                <span style={{ color: 'red', fontWeight: 'bold' }}>
                  This will delete any information already entered in the current project, and
                  create a new catcher template for this project.
                </span>
              </p>
            </div>
          </Modal>
          <Modal
            title="Clone Catcher Data"
            open={cloneCatcherDataModalOpen}
            onOk={handleCloneCatcherDataModalOk}
            onCancel={() => setCloneCatcherDataModalOpen(false)}
            style={{ top: '20%' }}>
            <div>
              <p>
                This will clone Catcher Data from the information mentioned below (Registry +
                Project ID + Crediting Period) and copies over the existing catcher data over to
                this project. <br></br>
                <span style={{ color: 'red', fontWeight: 'bold' }}>
                  This will delete any information already entered in the current project, and
                  replace it with the catcher content from the selected project.
                </span>
              </p>
            </div>
            <Form form={formClone} layout="vertical">
              <div className="flex gap-2">
                <Form.Item
                  label="Registry"
                  name="selectedGhgCode"
                  rules={[{ required: true, message: 'Please select GHG Standard!' }]}>
                  <Select>
                    {ghgStandards.map((standard) => (
                      <Select.Option key={standard?.id} value={standard.code}>
                        {standard.code}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item
                  label="Project ID"
                  name="selectedProjectId"
                  rules={[{ required: true, message: 'Please input Project ID!' }]}>
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Crediting Period"
                  name="selectedCreditingPeriod"
                  rules={[{ required: true, message: 'Please input Crediting Period!' }]}>
                  <Input type="number" />
                </Form.Item>
              </div>
            </Form>
          </Modal>
          <Modal
            title="Add Missing Captions Fields"
            open={addMissingCaptionOpen}
            onOk={handleMissingCaptionModalOk}
            onCancel={() => setAddMissingCaptionOpen(false)}
            style={{ top: '20%' }}>
            <div>
              <p>Upon confirmation, this will create the missing caption fields.</p>
            </div>
          </Modal>

          {/* <Select
            disabled={isReportCatcherLoading}
            defaultValue={project_crediting_period.project_report_catcher_status_platform?.[0]?.status}
            style={{ width: 120 }}
            onChange={updateProjectStatus}
          >
            <Select.Option value={ProjectStatus.REPORT_STAGING}>{BTN_TEXT[ProjectStatus.REPORT_STAGING]}</Select.Option>
            <Select.Option value={ProjectStatus.REPORT_PRODUCTION_LIVE}>{BTN_TEXT[ProjectStatus.REPORT_PRODUCTION_LIVE]}</Select.Option>
          </Select> */}
        </div>
      </div>
      {fields?.map((field, index) => {
        return (
          <RatingBlurbField
            field={field}
            project={project}
            key={index}
            entityId={entityId}
            queryKey={queryKey}
            workflowState={workflowState}
            handleDeepDiveToggleWithMessage={handleDeepDiveToggleWithMessage}
            onToggleTitle={onToggleTitle}
            onTitleChange={onDebouncedTitleChange}
            onSaveContent={onSaveContent}
          />
        );
      })}
    </Card>
  );
};

const Project: React.FC = () => {
  const { id, creditingPeriodId } = useParams<{ id: string; creditingPeriodId: string }>();
  const {
    token: { colorBgContainer }
  } = theme.useToken();

  const queryKey = `project_${id}_cp_${creditingPeriodId}`;

  const { isLoading, isError, data } = useQuery(queryKey, async () => {
    return await axiosInstance.get(`/catcher_projects/${id}/cp/${creditingPeriodId}`);
  });

  if (isLoading) {
    return (
      <Spin
        spinning={isLoading}
        indicator={<LoadingOutlined style={{ fontSize: 24, color: 'white' }} spin />}
      />
      // <Spin tip="Loading" size="large">
      //   <div className="content" />
      // </Spin>
    );
  } else if (isError) {
    return (
      <Alert
        message="Something went wrong"
        description="Something went wrong during loading the project details. Please wait for sometime, and refresh the page."
        type="error"
      />
    );
  }

  const projectData = data?.data?.data;

  if (!projectData) {
    return (
      <Alert
        message="Project not found"
        description="Please check the link you've clicked. No project found with the given id."
        type="info"
      />
    );
  }

  return (
    <>
      <WithProjectIdDetails project={projectData} queryKey={queryKey} />
    </>
  );
};

export default Project;
